import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent, FriendlyCaptchaComponent, InfoBannerComponent } from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { emailRegex } from '../../../../../../assets/regex';
import { PlatformAuthService } from '../../../../services/platform-auth.service';

type VerificationState = 'pending' | 'success' | 'failed' | null;

@Component({
  selector: 'app-platform-verify-email-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    ButtonComponent,
    FriendlyCaptchaComponent,
    InfoBannerComponent,
  ],
  templateUrl: './platform-verify-email-form.component.html',
  styleUrl: './platform-verify-email-form.component.scss',
})
export class PlatformVerifyEmailFormComponent {
  private readonly toastsService = inject(ToastsService);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly auth = inject(PlatformAuthService);

  readonly nonce = input<string | null>(null);

  readonly verifyEmailForm: FormGroup = this.fb.group({
    email: this.fb.control(null, [Validators.pattern(emailRegex), Validators.required]),
    captcha: this.fb.control(null, [Validators.required]),
  });
  verifyEmailFormSubmitAttempt = false;
  verificationState: VerificationState = null;
  private lastVerifiedNonce: string | null = null;

  constructor() {
    effect(() => {
      const nonce = this.nonce();
      if (!nonce || nonce === this.lastVerifiedNonce) {
        return;
      }
      this.lastVerifiedNonce = nonce;
      this.verificationState = 'pending';
      this.auth.verifyEmail(nonce).subscribe({
        next: () => {
          this.verificationState = 'success';
          this.cdr.markForCheck();
        },
        error: () => {
          this.verificationState = 'failed';
          this.cdr.markForCheck();
        },
      });
    });
  }

  /**
   * Submits the resend verification email form.
   */
  onVerifyEmailFormSubmit(): void {
    this.verifyEmailFormSubmitAttempt = true;

    if (this.verifyEmailForm.valid) {
      this.auth.sendEmailVerification(this.email.value, this.captchaCode.value ?? '').subscribe({
        next: () => {
          this.toastsService.success('jobboard.platform.auth.verify-email.success-toast');
          this.verifyEmailFormSubmitAttempt = false;
          this.verifyEmailForm.reset();
          this.cdr.markForCheck();
        },
        error: () => {
          this.captchaCode.setValue(null);
          this.toastsService.warning('jobboard.platform.auth.verify-email.error');
          this.cdr.markForCheck();
        },
      });
    }
  }

  get email() {
    return this.verifyEmailForm.get('email')!;
  }

  get captchaCode() {
    return this.verifyEmailForm.get('captcha') as FormControl<string | null>;
  }
}
